import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, ArrowUpRight, Mail } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import useReveal from '../hooks/useReveal';
import ServiceIcon from '../components/ServiceIcon';
import CTABand from '../components/CTABand';
import { SERVICES, CONTACT } from '../data/content';

export default function ServiceDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const cn = lang === 'zh' ? 'font-cn' : '';
  useReveal([lang, slug]);

  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/servicios" replace />;

  const data = service[lang];
  const others = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <div>
      {/* Header */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={service.image} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
          <div className="absolute inset-0 grain" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <Link to="/servicios" className={`inline-flex items-center gap-2 text-white/80 hover:text-[#C6A15B] transition-colors text-sm mb-6 ${cn}`}>
            <ArrowLeft className="w-4 h-4" /> {t('services.backToServices')}
          </Link>
          <div className="flex items-center gap-4 animate-fadeUp">
            <span className="flex items-center justify-center w-14 h-14 rounded-sm bg-[#C6A15B] text-[#0B1F3A]">
              <ServiceIcon name={service.icon} className="w-7 h-7" />
            </span>
            <h1 className={`font-serif-display text-4xl md:text-5xl font-semibold text-white text-balance ${cn}`}>{data.title}</h1>
          </div>
          <div className="gold-rule mt-6 animate-fadeUp" style={{ animationDelay: '0.2s' }} />
        </div>
      </section>

      {/* Content */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 reveal">
            <p className={`text-xl text-[#0B1F3A] font-medium leading-relaxed mb-6 ${cn}`}>{data.short}</p>
            <p className={`text-[#5c6470] leading-relaxed mb-10 ${cn}`}>{data.long}</p>

            <h3 className={`font-serif-display text-2xl font-semibold text-[#0B1F3A] mb-6 ${cn}`}>{t('services.keyPoints')}</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {data.points.map((p, i) => (
                <div key={i} className="flex items-start gap-3 bg-white rounded-sm p-4 border border-[#eadfca]">
                  <CheckCircle2 className="w-5 h-5 text-[#C8142E] shrink-0 mt-0.5" />
                  <span className={`text-[#0B1F3A] text-sm ${cn}`}>{p}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <aside className="reveal reveal-right">
            <div className="navy-gradient rounded-sm p-8 text-white sticky top-28">
              <h4 className={`font-serif-display text-xl font-semibold mb-3 ${cn}`}>{t('contact.eyebrow')}</h4>
              <p className={`text-white/70 text-sm mb-6 ${cn}`}>{t('contact.subtitle')}</p>
              <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 text-[#C6A15B] mb-6 text-sm">
                <Mail className="w-4 h-4" /> {CONTACT.email}
              </a>
              <Link to="/contacto" className={`inline-flex items-center gap-2 gold-btn rounded-full px-6 py-3 font-semibold w-full justify-center ${cn}`}>
                {t('nav.cta')} <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {/* Related */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <h3 className={`font-serif-display text-2xl font-semibold text-[#0B1F3A] mb-8 reveal ${cn}`}>{t('services.relatedTitle')}</h3>
          <div className="grid sm:grid-cols-3 gap-6">
            {others.map((s) => (
              <Link key={s.slug} to={`/servicios/${s.slug}`} className="svc-card lift reveal group bg-[#FAF8F3] rounded-sm border border-[#eadfca] overflow-hidden hover:shadow-xl">
                <div className="relative h-36 overflow-hidden">
                  <img src={s.image} alt="" className="svc-img w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3A]/70 to-transparent" />
                </div>
                <div className="p-5">
                  <h4 className={`font-serif-display text-lg font-semibold text-[#0B1F3A] group-hover:text-[#A9853E] transition-colors ${cn}`}>{s[lang].title}</h4>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
